import type { ReactNode } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useDocumentTitle } from "../../lib/useDocumentTitle";
import "./acme.css";

/**
 * Acme Analytics: the product of a fictional TryLook customer, with static sample data. Its AI guide
 * screen-shares these pages in demo calls (ACME_TOUR in src/worker/tour.py), so keep the two in step.
 */

const NAV = [
  { to: "/tour/acme/dashboard", label: "Dashboard" },
  { to: "/tour/acme/funnels", label: "Funnels" },
  { to: "/tour/acme/alerts", label: "Alerts" },
  { to: "/tour/acme/reports", label: "Reports" },
  { to: "/tour/acme/integrations", label: "Integrations" },
];

export function AcmeLayout() {
  return (
    <div className="acme">
      <aside className="acme-sidebar">
        <div className="acme-brand">
          <span className="acme-mark" aria-hidden="true" />
          Acme Analytics
        </div>
        <nav className="acme-nav" aria-label="Acme Analytics">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to}>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="acme-workspace">
          <span className="acme-workspace-name">Pinecrest Software</span>
          <span>Sample workspace</span>
        </div>
      </aside>
      <main className="acme-main">
        <Outlet />
      </main>
    </div>
  );
}

function PageHead({ title, subtitle }: { title: string; subtitle: string }) {
  useDocumentTitle(`${title} · Acme Analytics`);
  return (
    <header className="acme-head">
      <div>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
      <div className="acme-head-meta">
        <span className="acme-range">Last 90 days</span>
        <span className="acme-avatar" aria-hidden="true">
          JR
        </span>
      </div>
    </header>
  );
}

function Card({ title, children, tour }: { title: string; children: ReactNode; tour?: string }) {
  return (
    <section className="acme-card" data-tour={tour}>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

const KPIS = [
  { label: "Monthly recurring revenue", value: "$482,300", change: "+4.2% vs last month", trend: "up" },
  { label: "Net revenue retention", value: "118%", change: "+3 pts vs last quarter", trend: "up" },
  { label: "Active accounts", value: "1,284", change: "+38 this month", trend: "up" },
  { label: "Trial to paid", value: "22.4%", change: "−1.1 pts vs last month", trend: "down" },
];

const MRR = [
  { month: "Oct", value: 352 },
  { month: "Nov", value: 361 },
  { month: "Dec", value: 370 },
  { month: "Jan", value: 382 },
  { month: "Feb", value: 391 },
  { month: "Mar", value: 404 },
  { month: "Apr", value: 418 },
  { month: "May", value: 427 },
  { month: "Jun", value: 441 },
  { month: "Jul", value: 455 },
  { month: "Aug", value: 463 },
  { month: "Sep", value: 482 },
];

const WATCHLIST = [
  { account: "Harborview Logistics", mrr: "$18,400", change: "+32%", signal: "Expansion: seats used up 40%", good: true },
  { account: "Keystone Dental Group", mrr: "$9,750", change: "−18%", signal: "Churn risk: usage down 3 weeks", good: false },
  { account: "Brightwater Studios", mrr: "$7,200", change: "+12%", signal: "Upgraded to the Growth plan", good: true },
  { account: "Summit Ridge Foods", mrr: "$6,900", change: "−9%", signal: "Two failed payments", good: false },
];

function RevenueChart() {
  const width = 560;
  const height = 230;
  const pad = { top: 12, right: 12, bottom: 28, left: 48 };
  const [min, max] = [340, 500];
  const x = (index: number) => pad.left + (index * (width - pad.left - pad.right)) / (MRR.length - 1);
  const y = (value: number) => pad.top + ((max - value) * (height - pad.top - pad.bottom)) / (max - min);
  const line = MRR.map((point, index) => `${index === 0 ? "M" : "L"}${x(index).toFixed(1)},${y(point.value).toFixed(1)}`).join(" ");
  const baseline = height - pad.bottom;
  const area = `${line} L${x(MRR.length - 1).toFixed(1)},${baseline} L${x(0).toFixed(1)},${baseline} Z`;

  return (
    <svg
      className="acme-chart"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label="Monthly recurring revenue grew from $352k to $482k over the last 12 months"
    >
      {[350, 400, 450, 500].map((tick) => (
        <g key={tick}>
          <line className="acme-grid-line" x1={pad.left} x2={width - pad.right} y1={y(tick)} y2={y(tick)} />
          <text className="acme-axis" x={pad.left - 8} y={y(tick) + 4} textAnchor="end">
            ${tick}k
          </text>
        </g>
      ))}
      <path className="acme-area" d={area} />
      <path className="acme-line" d={line} />
      {MRR.map((point, index) => (
        <text key={point.month} className="acme-axis" x={x(index)} y={height - 8} textAnchor="middle">
          {point.month}
        </text>
      ))}
    </svg>
  );
}

export function AcmeDashboardPage() {
  return (
    <>
      <PageHead title="Dashboard" subtitle="Updated 12 minutes ago from Stripe, HubSpot and Segment." />
      <div className="acme-kpis">
        {KPIS.map((kpi) => (
          <div key={kpi.label} className="acme-kpi">
            <span className="acme-muted">{kpi.label}</span>
            <strong className="acme-kpi-value">{kpi.value}</strong>
            <span className={`acme-change is-${kpi.trend}`}>{kpi.change}</span>
          </div>
        ))}
      </div>
      <div className="acme-grid acme-grid-wide">
        <Card title="Monthly recurring revenue, last 12 months">
          <RevenueChart />
        </Card>
        <Card title="Accounts to watch" tour="accounts-to-watch">
          <ul className="acme-list">
            {WATCHLIST.map((row) => (
              <li key={row.account}>
                <div>
                  <span className="acme-strong">{row.account}</span>
                  <span className={row.good ? "acme-small is-good" : "acme-small is-bad"}>{row.signal}</span>
                </div>
                <div className="acme-right">
                  <span className="acme-strong">{row.mrr}</span>
                  <span className={row.good ? "acme-change is-up" : "acme-change is-down"}>{row.change}</span>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </>
  );
}

const FUNNEL = [
  { step: "Visited pricing page", count: 48_200, rate: "100%" },
  { step: "Started a trial", count: 6_940, rate: "14.4% of visitors" },
  { step: "Connected their data", count: 3_610, rate: "52.0% of trials" },
  { step: "Invited a teammate", count: 2_180, rate: "60.4% of those" },
  { step: "Became paying", count: 1_555, rate: "22.4% of trials" },
];

const CHANNELS = [
  { channel: "Partner referrals", rate: 31.8 },
  { channel: "Organic search", rate: 26.1 },
  { channel: "Paid search", rate: 17.3 },
  { channel: "Social", rate: 9.4 },
];

export function AcmeFunnelsPage() {
  const top = Math.max(...FUNNEL.map((row) => row.count));
  return (
    <>
      <PageHead title="Funnels" subtitle="From first visit to paying customer." />
      <div className="acme-grid acme-grid-split">
        <Card title="Pricing page to paid" tour="funnel">
          <ol className="acme-bars">
            {FUNNEL.map((row) => (
              <li key={row.step}>
                <div className="acme-bar-label">
                  <span className="acme-strong">{row.step}</span>
                  <span>
                    {row.count.toLocaleString("en-US")} <span className="acme-muted">· {row.rate}</span>
                  </span>
                </div>
                <div className="acme-bar">
                  <span style={{ width: `${Math.max(6, (row.count / top) * 100)}%` }} />
                </div>
              </li>
            ))}
          </ol>
        </Card>
        <Card title="Trial to paid by channel">
          <ol className="acme-bars">
            {CHANNELS.map((row) => (
              <li key={row.channel}>
                <div className="acme-bar-label">
                  <span className="acme-strong">{row.channel}</span>
                  <span>{row.rate}%</span>
                </div>
                <div className="acme-bar">
                  <span style={{ width: `${(row.rate / 40) * 100}%` }} />
                </div>
              </li>
            ))}
          </ol>
        </Card>
      </div>
    </>
  );
}

const RULES = [
  { name: "Churn risk", condition: "Weekly active users drop 30% on a paid account", notify: "Slack #customer-success", on: true },
  { name: "Expansion signal", condition: "Seat usage above 90% of the plan for 7 days", notify: "Email the account owner", on: true },
  { name: "Failed payments", condition: "Two failed charges within 14 days", notify: "Slack #finance", on: true },
  { name: "Stalled trial", condition: "No data connected 3 days after signup", notify: "Email the trial owner", on: false },
];

const RECENT = [
  { account: "Keystone Dental Group", rule: "Churn risk", detail: "Weekly active users down 34%", when: "2 hours ago" },
  { account: "Harborview Logistics", rule: "Expansion signal", detail: "46 of 50 seats in use", when: "Yesterday" },
  { account: "Summit Ridge Foods", rule: "Failed payments", detail: "Card declined twice", when: "2 days ago" },
];

export function AcmeAlertsPage() {
  return (
    <>
      <PageHead title="Alerts" subtitle="Know the moment an account needs attention." />
      <Card title="Alert rules" tour="alert-rules">
        <table className="acme-table">
          <thead>
            <tr>
              <th scope="col">Rule</th>
              <th scope="col">When</th>
              <th scope="col">Notify</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {RULES.map((rule) => (
              <tr key={rule.name}>
                <td className="acme-strong">{rule.name}</td>
                <td>{rule.condition}</td>
                <td>{rule.notify}</td>
                <td>
                  <span className={rule.on ? "acme-pill is-on" : "acme-pill is-off"}>{rule.on ? "On" : "Paused"}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Card title="Recent alerts">
        <ul className="acme-list">
          {RECENT.map((alert) => (
            <li key={alert.account}>
              <div>
                <span className="acme-strong">{alert.account}</span>
                <span className="acme-small">
                  {alert.rule}: {alert.detail}
                </span>
              </div>
              <span className="acme-muted">{alert.when}</span>
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}

const SCHEDULES = [
  { name: "Weekly board pack", cadence: "Mondays at 9:00 AM", delivery: "PDF and link to 6 people" },
  { name: "Monthly revenue review", cadence: "1st of every month", delivery: "Finance team" },
  { name: "Customer health digest", cadence: "Every day at 8:00 AM", delivery: "Slack #customer-success" },
];

const BOARD_PACK = [
  { label: "MRR", value: "$482.3k" },
  { label: "Net revenue retention", value: "118%" },
  { label: "New customers", value: "46" },
  { label: "Churned MRR", value: "$6.1k" },
];

export function AcmeReportsPage() {
  return (
    <>
      <PageHead title="Reports" subtitle="The numbers your team and board ask for, on a schedule." />
      <div className="acme-grid acme-grid-split">
        <Card title="Scheduled reports">
          <ul className="acme-list">
            {SCHEDULES.map((report) => (
              <li key={report.name}>
                <div>
                  <span className="acme-strong">{report.name}</span>
                  <span className="acme-small">{report.cadence}</span>
                </div>
                <span className="acme-muted">{report.delivery}</span>
              </li>
            ))}
          </ul>
        </Card>
        <Card title="Board pack preview" tour="board-pack">
          <div className="acme-board">
            {BOARD_PACK.map((item) => (
              <div key={item.label}>
                <span className="acme-muted">{item.label}</span>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>
          <p className="acme-summary">
            Growth this month came mostly from expansion in logistics and healthcare accounts. Churn stayed under 1.3%
            of MRR, and trial conversion dipped slightly after the pricing page change.
          </p>
        </Card>
      </div>
    </>
  );
}

const INTEGRATIONS = [
  { name: "Stripe", kind: "Billing", status: "Synced 12 min ago" },
  { name: "HubSpot", kind: "CRM", status: "Synced 20 min ago" },
  { name: "Segment", kind: "Product events", status: "Streaming live" },
  { name: "Snowflake", kind: "Data warehouse", status: "Synced 1 hour ago" },
  { name: "Salesforce", kind: "CRM", status: null },
  { name: "Chargebee", kind: "Billing", status: null },
  { name: "BigQuery", kind: "Data warehouse", status: null },
  { name: "Postgres", kind: "Database", status: null },
];

export function AcmeIntegrationsPage() {
  return (
    <>
      <PageHead title="Integrations" subtitle="Bring billing, CRM and product data together." />
      <div className="acme-integrations" data-tour="integrations">
        {INTEGRATIONS.map((integration) => (
          <section key={integration.name} className="acme-card acme-integration">
            <div className="acme-integration-head">
              <span className="acme-monogram" aria-hidden="true">
                {integration.name[0]}
              </span>
              <div>
                <h2>{integration.name}</h2>
                <span className="acme-muted">{integration.kind}</span>
              </div>
            </div>
            {integration.status ? (
              <span className="acme-small is-good">Connected · {integration.status}</span>
            ) : (
              <span className="acme-small">Not connected</span>
            )}
            <button type="button" className={integration.status ? "acme-button" : "acme-button is-primary"}>
              {integration.status ? "Manage" : "Connect"}
            </button>
          </section>
        ))}
      </div>
    </>
  );
}
